import { z } from 'zod';
import { SportNameSchema } from './profile.js';
import type { SportName } from './profile.js';
import {
  VenueOpeningHoursSchema,
  VenueSocialLinksSchema,
  VENUE_PHOTO_MIMES,
  VENUE_PHOTO_MAX_BYTES,
  VENUE_PHOTO_MAX_COUNT,
  type Venue,
  type VenuePhoto,
  type VenueSport,
} from './venue.js';
import type { SubVenue } from './sub-venue.js';
import type { VenuePricingBand } from './venue-pricing.js';
import type { VenueAmenityMaster } from './venue-amenity.js';
import type { VenueClosure } from './venue-closure.js';

/**
 * F12 venue-profile-editor composite types + Zod schemas.
 *
 * Decisions: see plans/12_F12_venue_profile_self_service.md (Decisions 1, 5,
 * 7, 8, 10, 11, 12, 13, 15).
 *
 * This module bundles every shape the portal editor consumes:
 *   - the read payload (`VenueProfileEditorPayload`) returned by
 *     GET /me/venues/:venueId/edit
 *   - the per-tab patch shapes (`UpdateVenueBasicsInputSchema`,
 *     `UpdateVenueOpeningHoursInputSchema`, `ReplaceVenueSportsInputSchema`)
 *   - the photo-variant shape stored in `venue_photos.variants` (jsonb)
 *   - the completeness response (`VenueProfileCompleteness`)
 *   - the change-history entry the History tab renders
 *   - the error-code union used by the API's 422/409 responses
 *
 * Per-resource schemas (sub-venue, pricing, amenity, closure) live in their
 * own sibling files and are re-exported from this file via the barrel
 * (`packages/types/src/index.ts`).
 */

// ─── Photo variants (Sharp pipeline output, stored in venue_photos.variants) ──

/**
 * Output of the Sharp pipeline (F12 Decision 6). Three WebP variants plus
 * the archived original. URLs are absolute CloudFront paths (no signing,
 * no expiry — public-read). NULL for pre-F12 photo rows; public reads fall
 * back to legacy `cdn_url` in that case.
 *
 * The canonical definition lives in `./venue.js` so it can be referenced
 * from `VenuePhoto` directly (avoiding a circular `import()` type expression
 * that tripped under NodeNext). Re-exported here so the existing
 * `@bravemove/types` import surface stays backward-compatible — the type was
 * originally exported from this file in F12 Section 1.
 */
export type { VenuePhotoVariants } from './venue.js';

// ─── Editor payload + completeness ───────────────────────────────────────────

export type VenueProfileCompletenessItem = {
  readonly key: string;
  readonly weight: number;
  readonly label: string;
  readonly satisfied: boolean;
};

export type VenueProfileCompleteness = {
  readonly percentage: number; // 0..100
  readonly satisfiedItems: ReadonlyArray<VenueProfileCompletenessItem>;
  readonly missingItems: ReadonlyArray<VenueProfileCompletenessItem>;
};

/**
 * Full editor read payload. Returned by GET /me/venues/:venueId/edit. The
 * `updatedAt` carried on the inner `venue` row is the value the client must
 * echo on the next mutation's `If-Match` header (per Decision 8). Pending
 * photos are included so the editor can show the pending strip; the public
 * detail endpoint filters them out.
 */
export type VenueProfileEditorPayload = {
  readonly venue: Venue;
  readonly sports: ReadonlyArray<VenueSport>;
  readonly subVenues: ReadonlyArray<SubVenue>;
  readonly pricing: ReadonlyArray<VenuePricingBand>;
  readonly amenities: ReadonlyArray<VenueAmenityMaster>;
  readonly closures: ReadonlyArray<VenueClosure>;
  readonly photos: ReadonlyArray<VenuePhoto>;
  readonly completeness: VenueProfileCompleteness;
  // F42 — admin-locked fields surfaced to the owner editor (read-only "Admin-
  // locked" badge + reason). Empty when no admin lock is active.
  readonly adminLockedFields: ReadonlyArray<{
    readonly fieldPath: string;
    readonly reason: string | null;
    readonly lockedAt: string;
  }>;
  /** F09 v2 — the venue tick (editor header). */
  readonly verified: boolean;
  /**
   * F09 v2 (E4) — the venue name is locked because the venue holds a tick;
   * the editor shows `NameLockedBanner` with a "Request name change" CTA.
   */
  readonly nameLocked: boolean;
};

// ─── Change history ──────────────────────────────────────────────────────────

export const VENUE_CHANGE_HISTORY_SURFACES = ['portal', 'admin', 'api', 'system'] as const;
export type VenueChangeHistorySurface = (typeof VENUE_CHANGE_HISTORY_SURFACES)[number];

/**
 * Every action name a {@link VenueChangeHistoryEntry} can carry.
 * The canonical wire format is dot-notation (snake_case_within_segments).
 *
 * Keep this list aligned with `history.actions.*` keys in
 * `packages/i18n/src/locales/en/venue-editor.json` — the conventional translation
 * step replaces `.` with `_` (e.g. `basics.updated` → `history.actions.basics_updated`).
 */
export const VENUE_CHANGE_ACTIONS = [
  'basics.updated',
  'opening_hours.updated',
  'sports.replaced',
  'sub_venue.added',
  'sub_venue.updated',
  'sub_venue.deleted',
  'pricing.replaced',
  'amenities.replaced',
  'closure.added',
  'closure.updated',
  'closure.deleted',
  'photo.uploaded',
  'photo.deleted',
  'photo.reordered',
  'photo.cover_set',
  'photo.approved',
  'photo.rejected',
  'photo.processing_failed',
] as const;

export type VenueChangeAction = (typeof VENUE_CHANGE_ACTIONS)[number];

export type VenueChangeHistoryEntry = {
  readonly id: string;
  readonly venueId: string;
  readonly actorUserId: string | null;
  readonly actorDisplayName: string | null; // resolved server-side; NULL for system + admin rows
  readonly surface: VenueChangeHistorySurface;
  readonly action: VenueChangeAction;
  readonly fieldPath: string | null;
  readonly oldValue: unknown;
  readonly newValue: unknown;
  readonly wasModerated: boolean;
  readonly appliedAt: string;
  readonly countryCode: string;
};

// ─── Error codes (422 / 409 union) ───────────────────────────────────────────

export const VENUE_PROFILE_ERROR_CODES = [
  'STALE_VENUE',
  'PRICING_BAND_OVERLAP',
  'PROTECTED_FIELD',
  'NOT_VENUE_OWNER',
  'PHOTO_QUOTA_EXCEEDED',
  'PHOTO_PROCESSING_FAILED',
  'PHOTO_REJECTED_BY_ADMIN',
  'CLOSURE_DATE_RANGE_INVALID',
  'SUB_VENUE_HAS_BOOKINGS',
  // FIX 5.2 — a suspended venue (or account) is read-only for its staff.
  'VENUE_SUSPENDED',
] as const;
export type VenueProfileErrorCode = (typeof VENUE_PROFILE_ERROR_CODES)[number];

// ─── Patch / replace input schemas ───────────────────────────────────────────

/**
 * Owner-allowed venue-row patch. Excludes every PROTECTED FIELD (per F12
 * Decision 11): name, slug, governorate_iso, caza_code, city_code, lat, lng,
 * status, tier, claim_status, owner_b2b_account_id, created_by_*,
 * merged_into_venue_id, cover_photo_id, country_code, tax_mode/rate.
 *
 * Phone is required to be E.164 (loose check at the type layer — strict
 * validation is the API's responsibility once we wire libphonenumber); the
 * service refuses an empty string because the F10 column is NOT NULL.
 */
export const UpdateVenueBasicsInputSchema = z
  .object({
    phone: z
      .string()
      .regex(/^\+\d{6,20}$/, 'phone must be E.164')
      .optional(),
    whatsappPhone: z
      .string()
      .regex(/^\+\d{6,20}$/, 'whatsappPhone must be E.164')
      .nullable()
      .optional(),
    streetAddress: z.string().min(1).max(240).nullable().optional(),
    description: z.string().max(500).nullable().optional(),
    socialLinks: VenueSocialLinksSchema.nullable().optional(),
  })
  .strict();
export type UpdateVenueBasicsInput = z.infer<typeof UpdateVenueBasicsInputSchema>;

export const UpdateVenueOpeningHoursInputSchema = z
  .object({
    openingHours: VenueOpeningHoursSchema.nullable(),
  })
  .strict();
export type UpdateVenueOpeningHoursInput = z.infer<typeof UpdateVenueOpeningHoursInputSchema>;

/**
 * Full replace of the venue's sport tags. The editor sends every selected
 * sport plus an optional `primarySport`. Service-layer logic enforces that
 * `primarySport` (if set) is one of the sports in the array, mirroring
 * F10's submission-payload superRefine.
 */
export const ReplaceVenueSportsInputSchema = z
  .object({
    sports: z.array(SportNameSchema).min(1, 'at least one sport is required').max(41),
    primarySport: SportNameSchema.optional(),
  })
  .strict()
  .superRefine((val, ctx) => {
    if (
      val.primarySport !== undefined &&
      !(val.sports as readonly SportName[]).includes(val.primarySport)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'primarySport must be one of the sports listed in sports[]',
        path: ['primarySport'],
      });
    }
  });
export type ReplaceVenueSportsInput = z.infer<typeof ReplaceVenueSportsInputSchema>;

// ─── Photo upload-url + finalize input schemas ───────────────────────────────

/**
 * `POST /me/venues/:venueId/photos/upload-url` — client requests a presigned
 * S3 PUT URL. Server validates mime + size + per-venue quota (10 photos
 * including pending) before issuing.
 */
export const RequestVenuePhotoUploadInputSchema = z
  .object({
    mime: z.enum(VENUE_PHOTO_MIMES),
    sizeBytes: z.number().int().positive().max(VENUE_PHOTO_MAX_BYTES),
    fileName: z.string().min(1).max(255).optional(),
  })
  .strict();
export type RequestVenuePhotoUploadInput = z.infer<typeof RequestVenuePhotoUploadInputSchema>;

/**
 * `POST /me/venues/:venueId/photos` — after client has PUT to the presigned
 * URL, this finalises by handing back the s3Key. Server fetches the object,
 * runs the Sharp pipeline, writes the 3 variants + original archive to S3,
 * and inserts the venue_photos row with moderation_status='pending'.
 */
export const FinalizeVenuePhotoInputSchema = z
  .object({
    s3Key: z.string().min(1).max(512),
    altText: z.string().max(255).optional(),
  })
  .strict();
export type FinalizeVenuePhotoInput = z.infer<typeof FinalizeVenuePhotoInputSchema>;

/**
 * Caption-only patch on an existing photo. Body is intentionally minimal —
 * mime/size are immutable post-upload.
 */
export const UpdateVenuePhotoInputSchema = z
  .object({
    altText: z.string().max(255).nullable().optional(),
  })
  .strict();
export type UpdateVenuePhotoInput = z.infer<typeof UpdateVenuePhotoInputSchema>;

export const ReorderVenuePhotosInputSchema = z
  .object({
    photoIds: z.array(z.string().uuid()).min(1).max(VENUE_PHOTO_MAX_COUNT),
  })
  .strict();
export type ReorderVenuePhotosInput = z.infer<typeof ReorderVenuePhotosInputSchema>;
